import React, {Dispatch, useCallback, useState} from 'react';
import {GridPaginationModel, GridSortModel} from '@mui/x-data-grid-pro';
import {windowIsDefined} from 'app/helpers/windowIsDefined';
import {desktopContentWidth} from 'app/styles/media';
import {Action, PlayerStatsAlltimeState} from 'app/components/team/playerstats/TeamPlayerStatsReducer';
import {
  fetchNextPlayers,
  updatePlayerStatsAlltimeSortmodel,
} from 'app/components/team/playerstats/TeamPlayerStatsActions';
import PlayerStatsGrid from 'app/components/team/playerstats/PlayerStatsGrid';

const MOBILE_OFFSET = 254;

interface PlayerStatsAlltimeGridProps {
  data: PlayerStatsAlltimeState;
  loadData: (subpage?: string, reload?: boolean, sortQuery?: string) => void;
  dispatch: Dispatch<Action>;
}
const PAGE_SIZE = 15;
export default function PlayerStatsAlltimeGrid({data, loadData, dispatch}: PlayerStatsAlltimeGridProps) {
  const {players: playersData, isFetching, error, sortModel} = data;
  const {data: players, links, meta} = playersData ?? {data: [], links: null, meta: null};
  const isDesktop = windowIsDefined && window.innerWidth >= desktopContentWidth;
  const [paginationModel, setPaginationModel] = useState({
    page: meta?.currentPage ? meta.currentPage - 1 : 0,
    pageSize: PAGE_SIZE,
  });

  const calcMaxHeight = useCallback(() => {
    if (windowIsDefined && window.innerWidth < desktopContentWidth) {
      return window.innerHeight - MOBILE_OFFSET;
    }
    if (!players.length || error) {
      return 350;
    }
    return '100%';
  }, [players, error]);

  const fetchNext = useCallback(
    (add: boolean = false) => {
      if (links?.next) {
        fetchNextPlayers(links.next, add)(dispatch);
      }
    },
    [links]
  );

  const fetchPrev = useCallback(() => {
    if (links?.prev) {
      fetchNextPlayers(links.prev, false)(dispatch);
    }
  }, [links]);

  const handleSortModelChange = React.useCallback((newSortModel: GridSortModel) => {
    dispatch(updatePlayerStatsAlltimeSortmodel(newSortModel));
    setPaginationModel({page: 0, pageSize: PAGE_SIZE});
    const sortQuery = newSortModel
      .map(
        sortValue =>
          `${sortValue.sort === 'desc' ? '-' : ''}${sortValue.field === 'penalties' ? 'penaltiesHit' : sortValue.field}`
      )
      .join(',');
    loadData('alltime', true, sortQuery);
  }, []);

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    if (newPaginationModel.page < paginationModel.page) {
      fetchPrev();
    } else {
      fetchNext();
    }
    setPaginationModel(newPaginationModel);
  };

  const handleScrollEnd = React.useCallback(() => {
    if (isDesktop) {
      return;
    }
    fetchNext(true);
  }, [fetchNext, isDesktop]);

  return (
    <PlayerStatsGrid
      type='alltime'
      players={players}
      isLoading={isFetching}
      error={error}
      calcMaxHeight={calcMaxHeight}
      withTopEleven={false}
      getRowHeight={() => 54}
      hideFooter={!isDesktop}
      pageSizeOptions={[PAGE_SIZE]}
      pagination={isDesktop}
      paginationMode='server'
      rowCount={meta?.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortingMode='server'
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onRowsScrollEnd={handleScrollEnd}
    />
  );
}
